import type {Evaluator} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {visit} from "#template/template-node/visit-helpers"
import {activationVisitorMarker} from "#template/role-markers"

// Wrapper-style operator: holds an input node (record/object-like) as its `input` field. The
// concrete dim-variant pins which input types are accepted.
export type AttributeSetParameters<TInput> = {
    name: string
    input: TInput
}

// One shared parameterized base — variants pin `T` and declare their dim-specific marker manually.
// `TInput` is the concrete input-node type accepted by the variant.
export abstract class GeometryAttributeSetBuilderNode<
    out T extends IndexingDim,
    out TInput extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, AttributeSetParameters<TInput>> {
    get [activationVisitorMarker]() {
        return true as const
    }

    public override onFilterActive(
        context: OnFilterActiveContext,
        evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<AttributeSetParameters<TInput>> {
        visit(this.parameters.input, evaluator)
        return super.onFilterActive(context, evaluator)
    }

    public override onCompile(context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<AttributeSetParameters<TInput>> {
        visit(this.parameters.input, evaluator)
        return super.onCompile(context, evaluator)
    }

    protected override hasValidParameters(
        parameters: AttributeSetParameters<TInput>,
    ): parameters is AttributeSetParameters<TInput> & NonNullableParameters<AttributeSetParameters<TInput>> {
        return parameters.input !== null && parameters.input !== undefined
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<AttributeSetParameters<TInput>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const evaluatedGeometryData = evaluator.evaluate(parameters.input)
        return {evaluatedGeometryData}
    }
}
